import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Cable, Eye, KeyRound, Link2, Plus, RefreshCw, RotateCcw, Send, Trash2, Webhook, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { Alert, Badge, Button, Card, CodeBlock, CopyButton, Empty, Field, Modal, PageHead, Select, Spinner, Switch, TextInput, cx, useToast } from "../components/ui";
import { useT, type Translate } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { useDates } from "../lib/useDates";
import { MigrationEntry } from "./Migration";

type Tab = "webhooks" | "hubspot" | "salesforce" | "zapier" | "import";
type EventType = S["CrmEventType"];
type Provider = S["CrmProvider"];

const TABS: { value: Tab; label: MessageKey | null }[] = [
  { value: "webhooks", label: "integrations.tab.webhooks" },
  { value: "hubspot", label: null },
  { value: "salesforce", label: null },
  { value: "zapier", label: "integrations.tab.zapier" },
  { value: "import", label: "integrations.tab.import" },
];
const PROVIDER_NAMES: Record<Tab, string> = { webhooks: "", hubspot: "HubSpot", salesforce: "Salesforce", zapier: "Zapier", import: "" };

const STAGE_EVENTS: { key: keyof S["CrmStageMap"]; label: MessageKey; hint: MessageKey }[] = [
  { key: "opportunity", label: "integrations.stage.opportunity", hint: "integrations.stage.opportunityHint" },
  { key: "revised", label: "integrations.stage.revised", hint: "integrations.stage.revisedHint" },
  { key: "renewal_requested", label: "integrations.stage.renewal", hint: "integrations.stage.renewalHint" },
  { key: "expired", label: "integrations.stage.expired", hint: "integrations.stage.expiredHint" },
];

/** Masked secrets (codes, never translated). */
const MASKED_SECRET = "whsec_••••••••••••••••••••••••••••••••";
const maskedKey = (last4: string) => `sk_live_…${last4}`;

const VERIFY_SNIPPET = `// Node.js (npm i standardwebhooks)
import { Webhook } from "standardwebhooks";
const wh = new Webhook(process.env.SOLAR_OS_WEBHOOK_SECRET); // whsec_…
const event = wh.verify(rawBody, {
  "webhook-id": req.headers["webhook-id"],
  "webhook-timestamp": req.headers["webhook-timestamp"],
  "webhook-signature": req.headers["webhook-signature"],
});
// Keep the highest event.data.sequence per event.data.lead_id; drop older ones.`;

/** "retry in 4 minutes" / "retry due" for a pending delivery. */
function retryIn(t: Translate, until: (at: string) => string, at: string): string {
  const seconds = (new Date(at).getTime() - Date.now()) / 1000;
  return seconds <= 30 ? t("integrations.delivery.retryDue") : t("integrations.delivery.retryIn", { when: until(at) });
}

const EVENT_DESCRIPTIONS: Record<string, MessageKey> = {
  "lead.created": "integrations.event.leadCreated",
  "lead.updated": "integrations.event.leadUpdated",
  "proposal.ready": "integrations.event.proposalReady",
  "opportunity.created": "integrations.event.opportunityCreated",
  "quote.revised": "integrations.event.quoteRevised",
  "quote.renewal_requested": "integrations.event.quoteRenewalRequested",
  "quote.expired": "integrations.event.quoteExpired",
};

/** What an event type means, in the reader's language (the API's English description for a type we do not know). */
function eventDescription(t: Translate, e: S["CrmEventTypeInfo"]): string {
  return EVENT_DESCRIPTIONS[e.type] ? t(EVENT_DESCRIPTIONS[e.type]) : e.description;
}

function deliveryTone(status: S["CrmDeliveryStatus"]): "ok" | "warn" | "danger" | "info" {
  return status === "succeeded" ? "ok" : status === "failed" ? "danger" : status === "pending" ? "warn" : "info";
}

function StatusBadge({ endpoint }: { endpoint: S["WebhookEndpoint"] }) {
  const t = useT();
  if (endpoint.enabled) return <Badge tone="ok">{t("integrations.endpoint.active")}</Badge>;
  const why = endpoint.disabled_reason === "failing" || endpoint.disabled_reason === "gone" ? endpoint.disabled_reason : "paused";
  return <Badge tone={endpoint.disabled_reason === "manual" ? "info" : "danger"}>{t("integrations.endpoint.disabled", { why })}</Badge>;
}

/** A delivery's status in words (pending, succeeded, failed, cancelled). */
function deliveryStatus(t: Translate, status: string): string {
  return t("integrations.delivery.status", { status });
}

function useOverview(orgId: string) {
  return useQuery({
    queryKey: ["integrations", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/integrations", { params: { path: { org_id: orgId } } })),
  });
}

/** The delivery log of a webhook endpoint or a CRM connector. */
function DeliveryLog({ items, loading, error, onReplay, replaying }: {
  items: S["CrmDelivery"][] | undefined;
  loading: boolean;
  error: unknown;
  onReplay?: (d: S["CrmDelivery"]) => void;
  replaying?: string | null;
}) {
  const t = useT();
  const dates = useDates();
  const intl = useIntl();
  if (loading) return <Spinner label={t("integrations.common.loading")} />;
  if (error) return <div className="card-body"><Alert tone="danger">{errorMessage(error)}</Alert></div>;
  if (!items?.length)
    return <div className="card-body small muted">{t("integrations.delivery.empty")}</div>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>{t("integrations.delivery.col.when")}</th>
            <th>{t("integrations.delivery.col.event")}</th>
            <th>{t("integrations.delivery.col.status")}</th>
            <th className="right">{t("integrations.delivery.col.response")}</th>
            <th className="right">{t("integrations.delivery.col.latency")}</th>
            <th className="right">{t("integrations.delivery.col.attempts")}</th>
            {onReplay && <th />}
          </tr>
        </thead>
        <tbody>
          {items.map((d) => (
            <tr key={d.id}>
              <td className="nowrap">
                <div className="cell-main">{dates.relative(d.last_attempt_at || d.created_at)}</div>
                <div className="cell-sub">{dates.dateTime(d.created_at)}</div>
              </td>
              <td>
                <code dir="ltr">{d.event_type}</code>
                {d.kind !== "event" && <Badge plain>{t("integrations.delivery.kind", { kind: d.kind })}</Badge>}
                {d.sequence != null && <div className="cell-sub">{t("integrations.delivery.sequence", { n: d.sequence })}</div>}
              </td>
              <td>
                <Badge tone={deliveryTone(d.status)}>{deliveryStatus(t, d.status)}</Badge>
                {d.status === "pending" && d.next_attempt_at && (
                  <div className="cell-sub">{retryIn(t, dates.until, d.next_attempt_at)}</div>
                )}
                {d.error && <div className="cell-sub ellipsis" dir="auto" style={{ maxWidth: 260 }} title={d.error}>{d.error}</div>}
                {!d.error && d.response_excerpt && d.target !== "webhook" && (
                  <div className="cell-sub ellipsis" dir="auto" style={{ maxWidth: 260 }} title={d.response_excerpt}>{d.response_excerpt}</div>
                )}
              </td>
              <td className="right">{d.response_status ?? "—"}</td>
              <td className="right nowrap">{d.latency_ms != null ? intl.formatNumber(d.latency_ms, { style: "unit", unit: "millisecond", unitDisplay: "short" }) : "—"}</td>
              <td className="right">{intl.formatNumber(d.attempts)}</td>
              {onReplay && (
                <td className="right">
                  <Button size="sm" variant="ghost" icon={<RotateCcw size={14} />} loading={replaying === d.id} onClick={() => onReplay(d)}>
                    {t("integrations.delivery.replay")}
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- webhooks
function EventPicker({ types, value, onChange }: { types: S["CrmEventTypeInfo"][]; value: EventType[]; onChange: (v: EventType[]) => void }) {
  const t = useT();
  const all = value.length === 0;
  return (
    <div className="stack" style={{ gap: 8 }}>
      <Switch checked={all} onChange={(on) => onChange(on ? [] : types.map((e) => e.type))} label={t("integrations.events.all")} />
      {!all && (
        <div className="stage-list" style={{ border: "1px solid var(--border)", borderRadius: "var(--radius-sm)" }}>
          {types.map((e) => (
            <label key={e.type} className="stage-item" style={{ gridTemplateColumns: "auto minmax(0, 1fr)", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={value.includes(e.type)}
                onChange={(ev) => onChange(ev.target.checked ? [...value, e.type] : value.filter((v) => v !== e.type))}
              />
              <div>
                <code dir="ltr">{e.type}</code>
                <div className="small muted">{eventDescription(t, e)}</div>
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function EndpointModal({ orgId, types, endpoint, onClose, onSaved }: {
  orgId: string;
  types: S["CrmEventTypeInfo"][];
  endpoint?: S["WebhookEndpoint"];
  onClose: () => void;
  onSaved: (secret?: string) => void;
}) {
  const t = useT();
  const [url, setUrl] = useState(endpoint?.url ?? "https://");
  const [description, setDescription] = useState(endpoint?.description ?? "");
  const [events, setEvents] = useState<EventType[]>(endpoint?.event_types ?? []);
  const path = { params: { path: { org_id: orgId } } };
  const save = useMutation({
    mutationFn: async () => {
      if (endpoint) {
        await unwrap(api.PATCH("/v1/orgs/{org_id}/webhooks/{endpoint_id}", {
          params: { path: { org_id: orgId, endpoint_id: endpoint.id } },
          body: { url, description: description || null, event_types: events },
        }));
        return undefined;
      }
      const created = await unwrap(api.POST("/v1/orgs/{org_id}/webhooks", { ...path, body: { url, description: description || null, event_types: events } }));
      return created.secret;
    },
    onSuccess: (secret) => onSaved(secret),
  });
  return (
    <Modal
      title={endpoint ? t("integrations.endpoint.editTitle") : t("integrations.endpoint.add")}
      onClose={onClose}
      width={640}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t("integrations.common.cancel")}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            {endpoint ? t("integrations.common.save") : t("integrations.endpoint.add")}
          </Button>
        </>
      }
    >
      <div className="stack">
        {save.error && <Alert tone="danger">{errorMessage(save.error)}</Alert>}
        <Field label={t("integrations.endpoint.url")} hint={t("integrations.endpoint.urlHint")}>
          <TextInput value={url} dir="ltr" onChange={(e) => setUrl(e.target.value)} placeholder={t("integrations.endpoint.urlPlaceholder")} autoFocus />
        </Field>
        <Field label={t("integrations.endpoint.description")} hint={t("integrations.endpoint.descriptionHint")}>
          <TextInput value={description} dir="auto" onChange={(e) => setDescription(e.target.value)} placeholder={t("integrations.endpoint.descriptionPlaceholder")} />
        </Field>
        <Field label={t("integrations.endpoint.events")}>
          <EventPicker types={types} value={events} onChange={setEvents} />
        </Field>
      </div>
    </Modal>
  );
}

function SecretBox({ orgId, endpoint }: { orgId: string; endpoint: S["WebhookEndpoint"] }) {
  const t = useT();
  const dates = useDates();
  const toast = useToast();
  const qc = useQueryClient();
  const [secret, setSecret] = useState<string | null>(null);
  const path = { params: { path: { org_id: orgId, endpoint_id: endpoint.id } } };
  const reveal = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/orgs/{org_id}/webhooks/{endpoint_id}/secret/reveal", path)),
    onSuccess: (s) => setSecret(s.secret),
    onError: (e) => toast(errorMessage(e), true),
  });
  const rotate = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/orgs/{org_id}/webhooks/{endpoint_id}/secret/rotate", path)),
    onSuccess: (s) => {
      setSecret(s.secret);
      qc.invalidateQueries({ queryKey: ["webhooks", orgId] });
      toast(t("integrations.secret.rotated"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  return (
    <Field
      label={t("integrations.secret.label")}
      hint={endpoint.secret_rotating_until ? t("integrations.secret.rotating", { date: dates.dateTime(endpoint.secret_rotating_until) }) : t("integrations.secret.hint")}
    >
      <div className="keybox">
        <code dir="ltr">{secret ?? MASKED_SECRET}</code>
        {secret ? <CopyButton text={secret} /> : <Button size="sm" icon={<Eye size={14} />} loading={reveal.isPending} onClick={() => reveal.mutate()}>{t("integrations.secret.reveal")}</Button>}
        <Button size="sm" variant="ghost" icon={<RefreshCw size={14} />} loading={rotate.isPending} onClick={() => window.confirm(t("integrations.secret.rotateConfirm")) && rotate.mutate()}>
          {t("integrations.secret.rotate")}
        </Button>
      </div>
    </Field>
  );
}

function EndpointDetail({ orgId, endpoint, types, canManage }: { orgId: string; endpoint: S["WebhookEndpoint"]; types: S["CrmEventTypeInfo"][]; canManage: boolean }) {
  const t = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [testType, setTestType] = useState<EventType>("lead.created");
  const [replaying, setReplaying] = useState<string | null>(null);
  const key = ["webhook-deliveries", endpoint.id];
  const path = { params: { path: { org_id: orgId, endpoint_id: endpoint.id } } };
  const log = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/webhooks/{endpoint_id}/deliveries", { params: { ...path.params, query: { limit: 25 } } })),
    refetchInterval: 15_000,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ["webhooks", orgId] });
  };
  const test = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/orgs/{org_id}/webhooks/{endpoint_id}/test", { ...path, body: { event_type: testType } })),
    onSuccess: (d) => {
      refresh();
      toast(
        d.status === "succeeded"
          ? t("integrations.test.delivered", { status: String(d.response_status ?? "—"), ms: d.latency_ms ?? 0 })
          : t("integrations.test.failed", { error: d.error ?? String(d.response_status ?? "—") }),
        d.status !== "succeeded",
      );
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const replay = useMutation({
    mutationFn: (d: S["CrmDelivery"]) => unwrap(api.POST("/v1/orgs/{org_id}/webhooks/{endpoint_id}/deliveries/{delivery_id}/replay", { params: { path: { ...path.params.path, delivery_id: d.id } } })),
    onMutate: (d) => setReplaying(d.id),
    onSettled: () => setReplaying(null),
    onSuccess: (d) => {
      refresh();
      toast(
        d.status === "succeeded" ? t("integrations.delivery.replayed") : t("integrations.delivery.replayFailed", { error: d.error ?? String(d.response_status ?? "—") }),
        d.status !== "succeeded",
      );
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  return (
    <Card
      title={<span className="row" style={{ gap: 8, flexWrap: "nowrap" }}><Webhook size={16} style={{ flex: "none" }} /> <span style={{ overflowWrap: "anywhere" }} dir="ltr">{endpoint.url}</span></span>}
      description={endpoint.description || (endpoint.source !== "dashboard" ? t("integrations.endpoint.subscribedThrough", { source: endpoint.source }) : undefined)}
      pad={false}
    >
      <div className="card-body stack">
        {!endpoint.enabled && endpoint.disabled_reason === "failing" && (
          <Alert tone="danger" title={t("integrations.endpoint.failingTitle")}>
            {t("integrations.endpoint.failingBody", { count: endpoint.pending_count })}
          </Alert>
        )}
        {canManage && (
          <div className="grid-2" style={{ alignItems: "end" }}>
            <SecretBox orgId={orgId} endpoint={endpoint} />
            <Field label={t("integrations.test.label")} hint={t("integrations.test.hint")}>
              <div className="row" style={{ flexWrap: "nowrap" }}>
                <Select<EventType> value={testType} onChange={setTestType} ariaLabel={t("integrations.test.eventType")} options={types.map((e) => ({ value: e.type, label: e.type }))} />
                <Button icon={<Send size={14} />} loading={test.isPending} onClick={() => test.mutate()}>
                  {t("integrations.test.send")}
                </Button>
              </div>
            </Field>
          </div>
        )}
      </div>
      <div className="divider" />
      <div className="card-head" style={{ paddingTop: 12, paddingBottom: 8 }}>
        <div>
          <h2 style={{ fontSize: 14 }}>{t("integrations.delivery.title")}</h2>
          <p>{t("integrations.delivery.description")}</p>
        </div>
      </div>
      <DeliveryLog items={log.data?.items} loading={log.isLoading} error={log.error} onReplay={canManage ? (d) => replay.mutate(d) : undefined} replaying={replaying} />
    </Card>
  );
}

function WebhooksTab({ orgId, overview }: { orgId: string; overview: S["IntegrationsOverview"] }) {
  const t = useT();
  const dates = useDates();
  const canManage = useOrg().can("integrations", "edit");
  const toast = useToast();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState<S["WebhookEndpoint"] | "new" | null>(null);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const endpoints = useQuery({
    queryKey: ["webhooks", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/webhooks", { params: { path: { org_id: orgId } } })),
  });
  const selectedId = params.get("endpoint");
  const items = endpoints.data?.items ?? [];
  const selected = items.find((e) => e.id === selectedId) ?? items[0];
  const toggle = useMutation({
    mutationFn: (e: S["WebhookEndpoint"]) =>
      unwrap(api.PATCH("/v1/orgs/{org_id}/webhooks/{endpoint_id}", { params: { path: { org_id: orgId, endpoint_id: e.id } }, body: { enabled: !e.enabled } })),
    onSuccess: (e) => {
      qc.invalidateQueries({ queryKey: ["webhooks", orgId] });
      toast(e.enabled ? t("integrations.endpoint.enabled") : t("integrations.endpoint.paused"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const remove = useMutation({
    mutationFn: (e: S["WebhookEndpoint"]) =>
      unwrap(api.DELETE("/v1/orgs/{org_id}/webhooks/{endpoint_id}", { params: { path: { org_id: orgId, endpoint_id: e.id } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["webhooks", orgId] });
      toast(t("integrations.endpoint.deleted"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (!overview.switches.webhooks)
    return <Alert tone="info" title={t("integrations.webhooks.notEnabled")}>{t("integrations.contactSupport")}</Alert>;
  return (
    <div className="stack">
      {newSecret && (
        <Alert tone="ok" title={t("integrations.endpoint.added")} action={<Button size="sm" variant="ghost" onClick={() => setNewSecret(null)}>{t("integrations.common.done")}</Button>}>
          {t("integrations.endpoint.secretIs")} <code dir="ltr">{newSecret}</code> <CopyButton text={newSecret} /> {t("integrations.endpoint.revealAgain")}
        </Alert>
      )}
      <Card
        title={t("integrations.webhooks.title")}
        description={t("integrations.webhooks.description")}
        actions={canManage && <Button variant="primary" icon={<Plus size={16} />} onClick={() => setEditing("new")}>{t("integrations.endpoint.add")}</Button>}
        pad={false}
      >
        {endpoints.isLoading ? (
          <Spinner label={t("integrations.common.loading")} />
        ) : endpoints.error ? (
          <div className="card-body"><Alert tone="danger">{errorMessage(endpoints.error)}</Alert></div>
        ) : !items.length ? (
          <Empty icon={<Webhook />} title={t("integrations.webhooks.empty")} action={canManage && <Button variant="primary" icon={<Plus size={16} />} onClick={() => setEditing("new")}>{t("integrations.endpoint.add")}</Button>}>
            {t("integrations.webhooks.emptyBody")}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("integrations.webhooks.col.endpoint")}</th>
                  <th>{t("integrations.webhooks.col.events")}</th>
                  <th>{t("integrations.webhooks.col.status")}</th>
                  <th>{t("integrations.webhooks.col.lastDelivery")}</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} className={cx("clickable", selected?.id === e.id && "selected")} onClick={() => setParams({ tab: "webhooks", endpoint: e.id })}>
                    <td style={{ maxWidth: 340 }}>
                      <div className="cell-main ellipsis" dir="ltr" title={e.url}>{e.url}</div>
                      <div className="cell-sub" dir="auto">
                        {e.description || "—"}
                        {e.source !== "dashboard" && <> · <Badge plain>{t("integrations.endpoint.source", { source: e.source })}</Badge></>}
                      </div>
                    </td>
                    <td className="small">
                      {e.event_types.length ? e.event_types.map((type) => <div key={type}><code dir="ltr">{type}</code></div>) : <span className="muted">{t("integrations.webhooks.allEvents")}</span>}
                    </td>
                    <td>
                      <StatusBadge endpoint={e} />
                      {e.pending_count > 0 && <div className="cell-sub">{t("integrations.webhooks.pending", { count: e.pending_count })}</div>}
                    </td>
                    <td className="nowrap">
                      {e.last_delivery_status ? <><Badge tone={deliveryTone(e.last_delivery_status as S["CrmDeliveryStatus"])}>{deliveryStatus(t, e.last_delivery_status)}</Badge><div className="cell-sub">{dates.relative(e.last_delivery_at)}</div></> : <span className="faint">—</span>}
                    </td>
                    {canManage && (
                      <td className="right nowrap" onClick={(ev) => ev.stopPropagation()}>
                        <Switch checked={e.enabled} onChange={() => toggle.mutate(e)} disabled={toggle.isPending} label={<span className="sr-only">{t("integrations.endpoint.enable", { url: e.url })}</span>} />
                        <Button size="sm" variant="ghost" onClick={() => setEditing(e)}>{t("integrations.common.edit")}</Button>
                        <Button size="sm" variant="ghost" icon={<Trash2 size={14} />} aria-label={t("integrations.common.delete")} onClick={() => window.confirm(t("integrations.endpoint.deleteConfirm", { url: e.url })) && remove.mutate(e)} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {selected && <EndpointDetail key={selected.id} orgId={orgId} endpoint={selected} types={overview.event_types} canManage={canManage} />}
      <Card
        title={t("integrations.webhooks.verifyTitle")}
        description={t("integrations.webhooks.signing", { id: "webhook-id", timestamp: "webhook-timestamp", signature: "webhook-signature", format: "v1,<base64 HMAC-SHA256 of id.timestamp.body>" })}
      >
        <CodeBlock code={VERIFY_SNIPPET} />
      </Card>
      {editing && (
        <EndpointModal
          orgId={orgId}
          types={overview.event_types}
          endpoint={editing === "new" ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={(secret) => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["webhooks", orgId] });
            if (secret) setNewSecret(secret);
            else toast(t("integrations.endpoint.saved"));
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- CRM connectors
function StageMapping({ orgId, provider, connection }: { orgId: string; provider: Provider; connection: S["CrmConnection"] }) {
  const t = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [pipelineId, setPipelineId] = useState<string | null>(connection.settings.pipeline_id ?? null);
  const [stages, setStages] = useState<S["CrmStageMap"]>(connection.settings.stages ?? {});
  const path = { params: { path: { org_id: orgId, provider } } };
  const catalog = useQuery({
    queryKey: ["crm-stages", orgId, provider],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/integrations/{provider}/stages", path)),
    retry: false,
  });
  const pipelines = catalog.data?.items ?? [];
  const pipeline = provider === "hubspot" ? pipelines.find((p) => p.id === pipelineId) ?? null : pipelines[0] ?? null;
  useEffect(() => {
    if (provider === "hubspot" && !pipelineId && pipelines.length) setPipelineId(pipelines[0].id);
  }, [provider, pipelineId, pipelines]);
  const save = useMutation({
    mutationFn: () => unwrap(api.PUT("/v1/orgs/{org_id}/integrations/{provider}/settings", { ...path, body: { pipeline_id: provider === "hubspot" ? pipelineId : undefined, stages } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["integrations", orgId] });
      toast(t("integrations.stages.saved"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const NONE = "__none__";
  return (
    <Card
      title={t("integrations.stages.title")}
      description={t("integrations.stages.description", { provider })}
      footer={<div className="row" style={{ justifyContent: "flex-end" }}><Button variant="primary" loading={save.isPending} onClick={() => save.mutate()} disabled={!pipeline}>{t("integrations.stages.save")}</Button></div>}
    >
      {catalog.isLoading ? (
        <Spinner label={t("integrations.stages.reading")} />
      ) : catalog.error ? (
        <Alert tone="danger" title={t("integrations.stages.readError")}>{errorMessage(catalog.error)}</Alert>
      ) : (
        <div className="stack">
          {provider === "hubspot" && (
            <Field label={t("integrations.stages.pipeline")}>
              <Select<string> value={pipelineId ?? ""} onChange={(v) => setPipelineId(v)} ariaLabel={t("integrations.stages.pipeline")} options={pipelines.map((p) => ({ value: p.id, label: p.label }))} />
            </Field>
          )}
          <div className="form-grid">
            {STAGE_EVENTS.map((s) => (
              <Field key={s.key} label={t(s.label)} hint={t(s.hint)}>
                <Select<string>
                  value={stages[s.key] ?? NONE}
                  ariaLabel={t(s.label)}
                  onChange={(v) => setStages({ ...stages, [s.key]: v === NONE ? null : v })}
                  options={[
                    { value: NONE, label: s.key === "opportunity" ? t("integrations.stages.first") : t("integrations.stages.leave") },
                    ...(pipeline?.stages ?? []).map((st) => ({ value: st.id, label: st.closed ? t("integrations.stages.closed", { stage: st.label }) : st.label })),
                  ]}
                />
              </Field>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

const CRM_COPY: Record<Provider, { name: string; what: MessageKey[] }> = {
  hubspot: {
    name: "HubSpot",
    what: ["integrations.hubspot.what1", "integrations.hubspot.what2", "integrations.hubspot.what3", "integrations.hubspot.what4"],
  },
  salesforce: {
    name: "Salesforce",
    what: ["integrations.salesforce.what1", "integrations.salesforce.what2", "integrations.salesforce.what3", "integrations.salesforce.what4"],
  },
};

function CrmTab({ orgId, provider, overview }: { orgId: string; provider: Provider; overview: S["IntegrationsOverview"] }) {
  const t = useT();
  const dates = useDates();
  const canManage = useOrg().can("integrations", "edit");
  const toast = useToast();
  const qc = useQueryClient();
  const [sandbox, setSandbox] = useState(false);
  const info = overview.providers.find((p) => p.provider === provider);
  const connection = overview.connections.find((c) => c.provider === provider);
  const copy = CRM_COPY[provider];
  const log = useQuery({
    queryKey: ["crm-deliveries", orgId, provider],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/integrations/{provider}/deliveries", { params: { path: { org_id: orgId, provider }, query: { limit: 25 } } })),
    enabled: !!connection,
    refetchInterval: 15_000,
  });
  const connect = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/orgs/{org_id}/integrations/{provider}/connect", { params: { path: { org_id: orgId, provider } }, body: { sandbox } })),
    onSuccess: (r) => window.location.assign(r.authorize_url),
    onError: (e) => toast(errorMessage(e), true),
  });
  const disconnect = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/orgs/{org_id}/integrations/{provider}", { params: { path: { org_id: orgId, provider } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["integrations", orgId] });
      toast(t("integrations.crm.disconnected", { provider: copy.name }));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (!info?.enabled)
    return <Alert tone="info" title={t("integrations.crm.notEnabled", { provider: copy.name })}>{t("integrations.contactSupport")}</Alert>;
  return (
    <div className="stack">
      <Card
        title={<span className="row" style={{ gap: 8 }}><Cable size={16} /> {copy.name}</span>}
        description={connection ? undefined : info.configured ? t("integrations.crm.connectIntro", { provider: copy.name }) : t("integrations.crm.comingSoonIntro", { provider: copy.name })}
        actions={
          connection
            ? canManage && <Button variant="danger" onClick={() => window.confirm(t("integrations.crm.disconnectConfirm", { provider: copy.name })) && disconnect.mutate()} loading={disconnect.isPending}>{t("integrations.crm.disconnect")}</Button>
            : !info.configured
              ? <Badge tone="info">{t("integrations.crm.comingSoon")}</Badge>
              : canManage && (
                  <Button variant="primary" icon={<Link2 size={16} />} loading={connect.isPending} onClick={() => connect.mutate()}>
                    {t("integrations.crm.connect", { provider: copy.name })}
                  </Button>
                )
        }
      >
        {connection ? (
          <div className="grid-2">
            <dl className="kv" style={{ alignSelf: "start" }}>
              <dt>{t("integrations.crm.status")}</dt>
              <dd><Badge tone={connection.status === "connected" ? "ok" : "danger"}>{t("integrations.crm.connectionStatus", { status: connection.status })}</Badge></dd>
              <dt>{t("integrations.crm.account")}</dt>
              <dd dir="auto">{connection.account_name || connection.account_id || "—"}</dd>
              {connection.instance_url && (<><dt>{t("integrations.crm.instance")}</dt><dd className="ellipsis" dir="ltr">{connection.instance_url.replace("https://", "")}</dd></>)}
              <dt>{t("integrations.crm.connected")}</dt>
              <dd>{dates.date(connection.connected_at)}</dd>
              <dt>{t("integrations.crm.lastSync")}</dt>
              <dd>{connection.last_synced_at ? dates.relative(connection.last_synced_at) : "—"}</dd>
            </dl>
            <ul className="small muted" style={{ margin: 0, paddingInlineStart: 18 }}>
              {copy.what.map((w) => <li key={w}>{t(w)}</li>)}
            </ul>
          </div>
        ) : (
          <div className="stack">
            <ul className="small muted" style={{ margin: 0, paddingInlineStart: 18 }}>
              {copy.what.map((w) => <li key={w}>{t(w)}</li>)}
            </ul>
            {provider === "salesforce" && info.configured && <Switch checked={sandbox} onChange={setSandbox} label={t("integrations.salesforce.sandbox")} />}
            {provider === "salesforce" && info.configured && <span className="hint">{t("integrations.salesforce.package")}</span>}
          </div>
        )}
        {connection?.last_error && <div style={{ marginTop: 12 }}><Alert tone="danger" title={t("integrations.crm.lastError")}>{connection.last_error}</Alert></div>}
      </Card>
      {connection && canManage && <StageMapping key={`${connection.provider}:${connection.account_id}`} orgId={orgId} provider={provider} connection={connection} />}
      {connection && (
        <Card title={t("integrations.crm.syncLog")} description={t("integrations.crm.syncLogDescription")} pad={false}>
          <DeliveryLog items={log.data?.items} loading={log.isLoading} error={log.error} />
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Zapier and API keys
function ZapierTab({ orgId, overview }: { orgId: string; overview: S["IntegrationsOverview"] }) {
  const t = useT();
  const dates = useDates();
  const canManage = useOrg().can("integrations", "edit");
  const toast = useToast();
  const qc = useQueryClient();
  const [label, setLabel] = useState("Zapier");
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const keys = useQuery({
    queryKey: ["api-keys", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/api-keys", { params: { path: { org_id: orgId } } })),
    enabled: canManage,
  });
  const create = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/orgs/{org_id}/api-keys", { params: { path: { org_id: orgId } }, body: { label } })),
    onSuccess: (k) => {
      setSecret(k.secret);
      setCreating(false);
      qc.invalidateQueries({ queryKey: ["api-keys", orgId] });
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/orgs/{org_id}/api-keys/{key_id}", { params: { path: { org_id: orgId, key_id: id } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["api-keys", orgId] });
      toast(t("integrations.keys.revoked"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  return (
    <div className="stack">
      {!overview.switches.zapier && <Alert tone="info" title={t("integrations.crm.notEnabled", { provider: "Zapier" })}>{t("integrations.contactSupport")}</Alert>}
      <Card title={<span className="row" style={{ gap: 8 }}><Zap size={16} /> Zapier</span>} description={t("integrations.zapier.description", { apps: 6000 })}>
        <ol className="small" style={{ margin: 0, paddingInlineStart: 18, lineHeight: 1.8 }}>
          <li>{t("integrations.zapier.step1")}</li>
          <li><FormattedMessage id="integrations.zapier.step2" values={{ b: (c) => <b>{c}</b> }} /></li>
          <li>{t("integrations.zapier.step3")}</li>
        </ol>
        <p className="small muted" style={{ marginBottom: 0 }}>{t("integrations.zapier.webhookNote")}</p>
      </Card>
      {canManage && (
        <Card
          title={<span className="row" style={{ gap: 8 }}><KeyRound size={16} /> {t("integrations.keys.title")}</span>}
          description={t("integrations.keys.description")}
          actions={<Button variant="primary" icon={<Plus size={16} />} onClick={() => setCreating(true)}>{t("integrations.keys.create")}</Button>}
          pad={false}
        >
          {secret && (
            <div className="card-body">
              <Alert tone="ok" title={t("integrations.keys.copyNow")} action={<Button size="sm" variant="ghost" onClick={() => setSecret(null)}>{t("integrations.common.done")}</Button>}>
                <div className="keybox" style={{ marginTop: 8 }}><code dir="ltr">{secret}</code><CopyButton text={secret} /></div>
              </Alert>
            </div>
          )}
          {keys.isLoading ? (
            <Spinner label={t("integrations.common.loading")} />
          ) : !keys.data?.items.length ? (
            <div className="card-body small muted">{t("integrations.keys.empty")}</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t("integrations.keys.col.name")}</th>
                    <th>{t("integrations.keys.col.key")}</th>
                    <th>{t("integrations.keys.col.created")}</th>
                    <th>{t("integrations.keys.col.lastUsed")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {keys.data.items.map((k) => (
                    <tr key={k.id}>
                      <td className="cell-main" dir="auto">{k.label || "—"}</td>
                      <td><code dir="ltr">{maskedKey(k.last4)}</code></td>
                      <td className="nowrap">{dates.date(k.created_at)}</td>
                      <td className="nowrap">{k.last_used_at ? dates.relative(k.last_used_at) : <span className="faint">{t("integrations.keys.never")}</span>}</td>
                      <td className="right"><Button size="sm" variant="ghost" icon={<Trash2 size={14} />} onClick={() => window.confirm(t("integrations.keys.revokeConfirm")) && revoke.mutate(k.id)}>{t("integrations.keys.revoke")}</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
      <Card title={t("integrations.api.title")} description={t("integrations.api.description")}>
        <CodeBlock code={`curl -H "X-Api-Key: sk_live_…" "${location.origin.replace("dashboard", "api")}/api/integrations/v1/quotes?email=ada@example.com"\n\nPOST   /api/integrations/v1/hooks          {"url": "...", "event_types": ["opportunity.created"]}\nDELETE /api/integrations/v1/hooks/{id}\nGET    /api/integrations/v1/events?type=lead.created\nPATCH  /api/integrations/v1/leads/{id}     {"status": "contacted"}`} />
      </Card>
      {creating && (
        <Modal
          title={t("integrations.keys.createTitle")}
          onClose={() => setCreating(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setCreating(false)}>{t("integrations.common.cancel")}</Button>
              <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>{t("integrations.keys.create")}</Button>
            </>
          }
        >
          <Field label={t("integrations.keys.name")} hint={t("integrations.keys.nameHint")}>
            <TextInput value={label} dir="auto" onChange={(e) => setLabel(e.target.value)} autoFocus />
          </Field>
        </Modal>
      )}
    </div>
  );
}

/** Settings › Integrations › Import: bring a company's data from another platform (OpenSolar today). */
function ImportTab() {
  const t = useT();
  return (
    <Card title={t("integrations.import.title")} description={t("integrations.import.description")}>
      <MigrationEntry />
    </Card>
  );
}

/** Integrations: webhooks, HubSpot, Salesforce, Zapier and API keys; Import (pages/Migration.tsx). */
export function Integrations() {
  const t = useT();
  const orgId = useOrgId();
  const toast = useToast();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((item) => item.value === params.get("tab"))?.value ?? (params.get("provider") as Tab | null) ?? (params.get("connected") as Tab | null) ?? "webhooks") as Tab;
  const overview = useOverview(orgId);
  useEffect(() => {
    const connected = params.get("connected");
    const error = params.get("error");
    if (connected) toast(t("integrations.crm.connectedToast", { provider: connected === "hubspot" ? "HubSpot" : "Salesforce" }));
    if (error) toast(error, true);
    if (connected || error) {
      qc.invalidateQueries({ queryKey: ["integrations", orgId] });
      setParams({ tab: connected || params.get("provider") || "webhooks" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <PageHead title={t("integrations.page.title")} description={t("integrations.page.description")} />
      <div className="tabs" role="tablist">
        {TABS.map((item) => (
          <button key={item.value} className={cx(tab === item.value && "on")} role="tab" aria-selected={tab === item.value} onClick={() => setParams({ tab: item.value })}>
            {item.label ? t(item.label) : PROVIDER_NAMES[item.value]}
            {(item.value === "hubspot" || item.value === "salesforce") && overview.data?.connections.some((c) => c.provider === item.value && c.status === "connected") && (
              <span className="faint" aria-label={t("integrations.tab.connected")}> ●</span>
            )}
          </button>
        ))}
      </div>
      {tab === "import" ? (
        <ImportTab />
      ) : overview.isLoading ? (
        <Spinner label={t("integrations.common.loading")} />
      ) : overview.error || !overview.data ? (
        <Alert tone="danger">{errorMessage(overview.error)}</Alert>
      ) : tab === "webhooks" ? (
        <WebhooksTab orgId={orgId} overview={overview.data} />
      ) : tab === "zapier" ? (
        <ZapierTab orgId={orgId} overview={overview.data} />
      ) : (
        <CrmTab key={tab} orgId={orgId} provider={tab} overview={overview.data} />
      )}
    </>
  );
}
